import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh'
import { useScrollCollapse } from '@/hooks/use-scroll-collapse'
import { useScrollMemory } from '@/hooks/use-scroll-memory'
import { useInboxScrollStore } from './inbox-scroll-store'
import { shouldRefresh } from '@/lib/gestures'
import { MobileConversationRow } from './mobile-conversation-row'
import { useState, useEffect, useRef, type MutableRefObject, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { Search, MoreHorizontal, MessageSquarePlus, UserPlus, CheckCheck, RefreshCw, Pin, Tag, X, MailOpen, Trash2, Check, Settings2, Loader2, Users, Star, ListChecks, ArrowDown } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ScrollArea, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import { PlatformBadge } from '@/components/shared/platform-mark'
import { ChannelChips, type ChannelFilterValue } from '@/components/shared/channel-chips'
import type { ChannelGroup } from '@/lib/channel-groups'
import { cn, initials } from '@/lib/utils'
import { api, apiError } from '@/lib/api-client'
import { useQueryClient } from '@tanstack/react-query'
import { useTags, useMyPermissions } from '@/hooks/use-settings'
import {
  useMarkUnread,
  useDeleteConversation,
  useDirectorySearch,
  useOpenDirectoryThread,
  useSetConversationTab,
  type ConversationListItem,
  type ConvTab,
  type DirectoryHit,
} from '@/hooks/use-conversations'
import { ManageTagsDialog, useTagPerms } from './conversation-tags'
import { formatRelativeTime, messagePreview } from './lib'
import { BulkActionBar, RowCheckbox, useLongPress } from './conversation-bulk'
import { AiAgentChip } from './ai-agent-chip'
import { accountColor, accountLabel, channelTitle } from './channel-label'
import type { ChannelGroupId } from '@/lib/channel-groups'

const NO_GROUPS: ReadonlySet<ChannelGroupId> = new Set()


interface Props {
  /** Tiêu đề cột = tên bộ lọc đang chọn ("Tất cả", "Chưa đọc"…). */
  title: string
  /** Số hiện cạnh tiêu đề (0 = ẩn). */
  titleCount?: number
  /** 'unread' (mặc định) = số chưa đọc, nền đỏ; 'total' = số hội thoại của bộ lọc đang mở, nền trung tính. */
  titleCountTone?: 'unread' | 'total'
  /** Hàng phụ ngay dưới đầu cột (vd chọn nhân viên AI ở bộ lọc "Nhân viên AI"). */
  subHeader?: ReactNode
  /** Bộ lọc "Nhân viên AI": mỗi dòng hiện nhân viên AI + trạng thái. */
  showAiAgent?: boolean
  /** Nhóm kênh có từ 2 tài khoản trở lên → dòng hiện tên tài khoản (OA / Fanpage…) — channel-label.ts. */
  manyAccountGroups?: ReadonlySet<ChannelGroupId>
  /** Nút thêm ở hàng tiêu đề, trước menu … (vd nút phễu Lọc). */
  headerActions?: ReactNode
  /**
   * Điện thoại: thay TOÀN BỘ khối tiêu đề + tìm kiếm + chip/tab (xem mobile-inbox-header.tsx). Nhận `collapsed` —
   * true khi danh sách đã cuộn xuống — để header tự thu gọn phần tên/kênh, giữ nguyên bộ lọc (xem chỗ gọi `onScroll`
   * bên dưới gắn vào viewport cuộn thật của Radix ScrollArea).
   */
  mobileHeader?: (collapsed: boolean) => ReactNode
  /** Thay trạng thái trống mặc định (vd AI Agent chưa có nhân viên AI → mời tạo). */
  emptyOverride?: ReactNode
  items: ConversationListItem[]
  activeId?: string
  search: string
  tag?: string
  isLoading: boolean
  isError: boolean
  /** Còn hội thoại ở trang sau chưa tải → cuộn tới cuối danh sách thì gọi `onLoadMore`. */
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  /** Bấm "Thử lại" ở màn lỗi. */
  onRetry?: () => void
  onSearchChange: (value: string) => void
  onTagChange: (tag?: string) => void
  onSelect: (id: string) => void
  onAddFriend: () => void
  /** Mở hộp thoại "Bắt đầu cuộc trò chuyện mới". */
  onNewConversation: () => void
  onMarkAllRead: () => void
  onRefresh: () => void
  /** Tab đang mở: 'main' = Ưu tiên, 'internal' = Nội bộ. */
  tab: ConvTab
  onTabChange: (tab: ConvTab) => void
  /** Số hội thoại chưa đọc của từng tab — hiện cạnh nhãn tab. */
  tabUnread: Record<ConvTab, number>
  /** Nhóm kênh có tài khoản — nguồn cho hàng chip lọc kênh. */
  channelGroups: ChannelGroup[]
  channel: ChannelFilterValue
  onChannelChange: (v: ChannelFilterValue) => void
  /**
   * Điện thoại: nhớ vị trí cuộn qua lần đóng/mở hội thoại (ref sống ở component cha — conversations-page.tsx,
   * không unmount — xem use-scroll-memory.ts). Không truyền → không nhớ/khôi phục gì (vd màn tích hợp sẵn khác
   * dùng chung component này mà không cần hành vi này).
   */
  scrollMemory?: MutableRefObject<number>
}

const CONV_TABS: { value: ConvTab; label: string; hint: string }[] = [
  { value: 'main', get label() { return i18n.t('convList:mhdr.tabMain') }, get hint() { return i18n.t('convList:list.tabMainHint') } },
  { value: 'internal', get label() { return i18n.t('convList:mhdr.tabInternal') }, get hint() { return i18n.t('convList:list.tabInternalHint') } },
]

export function ConversationList({
  title,
  titleCount,
  titleCountTone = 'unread',
  subHeader,
  showAiAgent,
  manyAccountGroups,
  headerActions,
  mobileHeader,
  emptyOverride,
  items,
  activeId,
  search,
  tag,
  isLoading,
  isError,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
  onSearchChange,
  onTagChange,
  onSelect,
  onAddFriend,
  onNewConversation,
  onMarkAllRead,
  onRefresh,
  tab,
  onTabChange,
  tabUnread,
  channelGroups,
  channel,
  onChannelChange,
  scrollMemory,
}: Props) {
  const { t } = useTranslation('convList')
  const isMobile = useIsMobile()
  const listRef = useRef<HTMLDivElement>(null)

  // Cuộn tới cuối danh sách → tải trang hội thoại kế. IntersectionObserver tính cả phần bị khung cuộn che nên không
  // cần biết phần tử cuộn thật (Radix ScrollArea). Dựng lại mỗi khi tải xong (items đổi) để, nếu dấu cuối vẫn còn
  // trong tầm nhìn (trang chưa đầy khung), lập tức tải tiếp thay vì đứng yên.
  const moreRef = useRef<HTMLLIElement>(null)
  useEffect(() => {
    const el = moreRef.current
    if (!el || !hasMore || loadingMore || !onLoadMore) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadMore()
    }, { rootMargin: '240px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loadingMore, onLoadMore, items.length])
  const ptr = usePullToRefresh(listRef, onRefresh, isMobile)
  // Không truyền scrollMemory (vd nơi khác dùng chung component này) → dùng ref nội bộ, coi như không nhớ gì
  // (mỗi lần mount đều là "lần đầu", memory luôn 0 — useScrollMemory bị tắt qua tham số `enabled` thứ 3).
  const fallbackScrollMemory = useRef(0)
  useScrollMemory(listRef, scrollMemory ?? fallbackScrollMemory, !!scrollMemory)
  // Bấm lại tab "Nhắn tin" đang active ở thanh điều hướng dưới (mobile-tab-bar.tsx) → về đầu danh sách, kiểu
  // Zalo. Bỏ qua lần chạy đầu (lúc mount) — chỉ phản ứng khi tín hiệu đổi khác giá trị lúc mount.
  // So sánh với 1 "ảnh chụp" resetSignal lúc mount (useRef(resetSignal) chỉ nhận giá trị ở lần đầu) thay vì
  // một cờ boolean bị set ở trong effect: cờ boolean bị React StrictMode (dev) gọi lại effect 2 lần phá vỡ —
  // lần gọi thứ 2 thấy cờ đã false nên tưởng không phải lần đầu, tự ý cuộn danh sách về đầu luôn.
  const resetSignal = useInboxScrollStore((s) => s.resetSignal)
  const mountResetSignal = useRef(resetSignal)
  useEffect(() => {
    if (resetSignal === mountResetSignal.current) return
    const el = listRef.current
    const viewport = el?.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]')
    if (!viewport) return
    viewport.scrollTop = 0
    if (scrollMemory) scrollMemory.current = 0
  }, [resetSignal, scrollMemory])
  // Cuộn xuống khỏi đầu danh sách → header điện thoại thu gọn phần tên/kênh, giữ nguyên bộ lọc (mobile-inbox-header.tsx).
  const headerCollapsed = useScrollCollapse(listRef, isMobile && !!mobileHeader)
  const { data: tags = [] } = useTags()
  const activeTag = tags.find((t) => t.name === tag)
  // Quyền hiệu lực (BE vẫn chốt lại) — "Xoá hội thoại" theo conversations.delete,
  // admin bật/tắt cho từng vai trò ở Đội ngũ → Vai trò & quyền.
  const { data: myPerms } = useMyPermissions()
  const canDelete = myPerms?.has('conversations.delete') ?? false

  // ── Chọn nhiều (thao tác hàng loạt) ──────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const exitSelect = () => { setSelectMode(false); setSelected([]) }
  const toggleSelected = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  // Bấm ô tích (hiện khi rê chuột) hoặc nhấn giữ trên điện thoại: vào chế độ
  // chọn và tích luôn dòng đó.
  const startSelect = (id: string) => {
    setSelectMode(true)
    setSelected((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }
  // Đổi bộ lọc là danh sách đổi — giữ lựa chọn cũ thì dễ thao tác nhầm lên
  // những hội thoại không còn nhìn thấy.
  useEffect(() => { exitSelect() }, [tab, channel, tag, search])
  useEffect(() => {
    if (!selectMode) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') exitSelect() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectMode])

  // Tra màu nhãn theo tên để hiển thị đúng màu đã cấu hình
  const tagColor = (name: string) => tags.find((t) => t.name === name)?.color ?? '#64748b'

  // Ô tìm kiếm với cả sang bạn bè / nhóm ĐÃ ĐỒNG BỘ nhưng chưa có hội thoại —
  // để nhắn trước cho khách mà không phải chờ khách nhắn vào.
  const directory = useDirectorySearch(search)
  const openThread = useOpenDirectoryThread()
  const [openingId, setOpeningId] = useState<string | null>(null)

  // Người/nhóm đã có hội thoại thì đằng nào cũng nằm trong danh sách trên,
  // hiện lại ở đây chỉ tổ trùng.
  const newHits: DirectoryHit[] = [
    ...(directory.data?.friends ?? []),
    ...(directory.data?.groups ?? []),
  ].filter((h) => !h.conversationId)

  const handleOpenHit = (hit: DirectoryHit) => {
    setOpeningId(hit.threadId)
    openThread.mutate(
      { channelAccountId: hit.account.id, threadId: hit.threadId, threadType: hit.threadType },
      {
        onSuccess: (r) => onSelect(r.conversationId),
        onError: (e) => toast.error(apiError(e)),
        onSettled: () => setOpeningId(null),
      },
    )
  }

  return (
    <div className="flex h-full flex-col">
      {isMobile && mobileHeader ? (
        mobileHeader(headerCollapsed)
      ) : (
        <>
      {/* Hàng đầu: tiêu đề bộ lọc + số chưa đọc + menu …; dưới là ô tìm kiếm (kiểu SaleSmartly) */}
      <div className="space-y-2.5 px-3 pt-3">
        <div className="flex h-8 items-center gap-2">
          <h2 className="truncate text-base font-medium">{title}</h2>
          {titleCount ? (
            <span
              className={cn(
                'flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold leading-none',
                titleCountTone === 'total' ? 'bg-muted text-muted-foreground' : 'bg-destructive text-destructive-foreground',
              )}
              title={titleCountTone === 'total' ? t('list.count', { count: titleCount }) : t('shared.unreadCount', { count: titleCount })}
            >
              {titleCountTone === 'total' ? titleCount : titleCount > 99 ? '99+' : titleCount}
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-0.5">
            {headerActions}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('mhdr.newConv')}
                  className="shrink-0"
                  onClick={onNewConversation}
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('mhdr.newConv')}</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('list.moreActions')} className="shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={onNewConversation}>
                  <MessageSquarePlus className="mr-2 h-4 w-4" />
                  {t('mhdr.newConv')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAddFriend}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t('mhdr.addFriend')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMarkAllRead}>
                  <CheckCheck className="mr-2 h-4 w-4" />
                  {t('mhdr.markAllRead')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectMode(true)}>
                  <ListChecks className="mr-2 h-4 w-4" />
                  {t('list.selectMultiple')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onRefresh}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('common:actions.refresh')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="relative">
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('mhdr.searchPlaceholder')}
            className="border-transparent bg-secondary pr-8 hover:bg-card focus-visible:bg-card"
          />
          <Search className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>

        {/* Nhãn đang lọc (chọn ở cột bộ lọc bên trái) — bấm × để bỏ */}
        {activeTag && (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 text-xs">
              <Tag className="h-3.5 w-3.5" style={{ color: activeTag.color }} />
              <span className="max-w-[160px] truncate font-medium">{activeTag.name}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onTagChange(undefined)}
                aria-label={t('list.clearTag')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Hai tab Ưu tiên / Nội bộ kiểu Zalo. Lọc nhóm kênh (ChannelChips) chuyển lên cột bộ lọc bên trái
          (FilterRail → account-rail.tsx, cùng hàng chọn tài khoản) — máy tính không còn hai nơi chọn kênh. */}
      {/* Điện thoại: chip kênh vẫn ở đây (hàng chip bộ lọc đã nằm dưới ô tìm kiếm riêng, không dùng FilterRail) */}
      <div className={cn('border-b px-3 pb-2.5 pt-2', isMobile && 'flex items-center gap-2 pb-2')}>
        {isMobile && channelGroups.length > 1 && (
          <ChannelChips
            groups={channelGroups}
            value={channel}
            onChange={onChannelChange}
            className="order-2 min-w-0 flex-1 pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          />
        )}
        <div role="tablist" aria-label={t('list.tablist')} className="flex shrink-0 gap-2">
          {CONV_TABS.map((ct) => {
            const active = tab === ct.value
            const unreadCount = tabUnread[ct.value]
            return (
              <button
                key={ct.value}
                type="button"
                role="tab"
                aria-selected={active}
                title={ct.hint}
                onClick={() => onTabChange(ct.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 text-sm transition-colors',
                  isMobile ? 'h-7 px-2.5 text-[13px]' : 'h-8',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'bg-accent font-medium text-primary'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
                )}
              >
                {ct.label}
                {unreadCount > 0 && (
                  <span className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
        </>
      )}

      {selectMode && (
        <BulkActionBar
          selectedIds={selected}
          visibleIds={items.map((c) => c.id)}
          tab={tab}
          tags={tags}
          canDelete={canDelete}
          onSelectAll={(all) => setSelected(all ? items.map((c) => c.id) : [])}
          onExit={exitSelect}
        />
      )}

      {subHeader}

      {/* Danh sách */}
      {/* Điện thoại: kéo xuống ở đầu danh sách để làm mới */}
      <div ref={listRef} className="relative flex min-h-0 flex-1 flex-col">
      {isMobile && (ptr.pull > 0 || ptr.refreshing) && (
        <div className="flex shrink-0 items-center justify-center overflow-hidden text-muted-foreground" style={{ height: ptr.pull }} role="status" aria-label={ptr.refreshing ? t('list.refreshing') : t('list.pullToRefresh')}>
          {ptr.refreshing ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowDown className="h-5 w-5 transition-transform" style={{ transform: `rotate(${shouldRefresh(ptr.pull) ? 180 : 0}deg)`, opacity: Math.min(1, ptr.pull / 40) }} />}
        </div>
      )}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <Loading label={t('list.loading')} />
        ) : isError ? (
          <ErrorState
            message={t('list.loadError')}
            hint={t('list.retryHint')}
            onRetry={onRetry}
          />
        ) : items.length === 0 ? (
          emptyOverride ? (
            emptyOverride
          ) : tab === 'internal' ? (
            <EmptyState
              title={t('list.emptyInternalTitle')}
              description={t('list.emptyInternalDesc')}
            />
          ) : (
            <EmptyState title={t('list.emptyTitle')} description={t('list.emptyDesc')} />
          )
        ) : (
          <ul className={isMobile ? 'pb-2' : 'divide-y'}>
            {items.map((c) => isMobile ? (
              <MobileConversationRow
                key={c.id}
                conv={c}
                active={c.id === activeId}
                onClick={() => onSelect(c.id)}
                selectMode={selectMode}
                checked={selected.includes(c.id)}
                onToggle={() => toggleSelected(c.id)}
                onStartSelect={() => startSelect(c.id)}
                canDelete={canDelete}
                showAiAgent={showAiAgent}
                accountName={accountLabel(c.channelAccount, manyAccountGroups ?? NO_GROUPS)}
              />
            ) : (
              <ConversationRow
                key={c.id}
                conv={c}
                active={c.id === activeId}
                tagColor={tagColor}
                onClick={() => onSelect(c.id)}
                canDelete={canDelete}
                selectMode={selectMode}
                checked={selected.includes(c.id)}
                onToggle={() => toggleSelected(c.id)}
                onStartSelect={() => startSelect(c.id)}
                showAiAgent={showAiAgent}
                accountName={accountLabel(c.channelAccount, manyAccountGroups ?? NO_GROUPS)}
              />
            ))}
            {hasMore && (
              <li ref={moreRef} className="flex h-12 items-center justify-center" aria-hidden={!loadingMore}>
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label={t('list.loading')} />}
              </li>
            )}
          </ul>
        )}

        {/* Bạn bè / nhóm đã đồng bộ nhưng chưa từng nhắn tin. Chỉ hiện khi
            đang tìm kiếm — để danh sách thường không bị ngập dòng trống. */}
        {newHits.length > 0 && (
          <div className="border-t">
            <p className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('list.newHits')}
            </p>
            <ul className="divide-y">
              {newHits.map((h) => (
                <li key={`${h.account.id}:${h.threadId}`}>
                  <button
                    type="button"
                    onClick={() => handleOpenHit(h)}
                    disabled={openingId === h.threadId}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/60 disabled:opacity-60"
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      {h.avatarUrl && <AvatarImage src={h.avatarUrl} alt={h.name} />}
                      <AvatarFallback>{initials(h.name)}</AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{h.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {h.threadType === 'group'
                          ? t('list.groupN', { count: h.memberCount ?? 0 })
                          : h.phone || t('list.friend')}
                        {' · '}
                        {h.account.displayName || t('list.zaloAccount')}
                      </span>
                    </span>
                    {openingId === h.threadId && (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ScrollArea>
      </div>
    </div>
  )
}

function ConversationRow({
  conv,
  active,
  tagColor,
  onClick,
  canDelete,
  selectMode,
  checked,
  onToggle,
  onStartSelect,
  showAiAgent,
  accountName,
}: {
  conv: ConversationListItem
  active: boolean
  showAiAgent?: boolean
  accountName?: string | null
  tagColor: (name: string) => string
  onClick: () => void
  canDelete: boolean
  /** Đang ở chế độ chọn nhiều: bấm dòng = tích/bỏ tích thay vì mở hội thoại. */
  selectMode: boolean
  checked: boolean
  onToggle: () => void
  /** Bấm ô tích khi chưa ở chế độ chọn, hoặc nhấn giữ trên điện thoại. */
  onStartSelect: () => void
}) {
  const { t } = useTranslation('convList')
  const longPress = useLongPress(onStartSelect)
  const unread = conv.unreadCount > 0
  const last = conv.messages?.[0]
  const name = conv.displayName || conv.contact?.fullName || t('shared.unnamed')
  const tags = conv.contact?.tags ?? []

  const markUnread = useMarkUnread()
  const deleteConv = useDeleteConversation()
  const setTab = useSetConversationTab()
  const isInternal = conv.tab === 'internal'
  const qc = useQueryClient()
  const { data: allTags = [] } = useTags()
  const { canManage: canManageTags } = useTagPerms()
  const [manageTagsOpen, setManageTagsOpen] = useState(false)
  const [savingTag, setSavingTag] = useState<string | null>(null)

  /** Gắn/gỡ nhãn ngay trong menu — API thay TOÀN BỘ mảng tags của contact. */
  async function toggleTag(tagName: string) {
    if (!conv.contact?.id || savingTag) return
    const has = tags.includes(tagName)
    const next = has ? tags.filter((x) => x !== tagName) : [...tags, tagName]
    setSavingTag(tagName)
    try {
      await api.put(`/contacts/${conv.contact.id}`, { tags: next })
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(has ? t('list.tagRemoved', { tag: tagName }) : t('list.tagAdded', { tag: tagName }))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSavingTag(null)
    }
  }

  async function handleMarkUnread() {
    try {
      await markUnread.mutateAsync(conv.id)
      toast.success(t('mrow.markedUnread'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleMoveTab() {
    const next: ConvTab = isInternal ? 'main' : 'internal'
    try {
      await setTab.mutateAsync({ id: conv.id, tab: next })
      toast.success(next === 'internal' ? t('mrow.movedInternal') : t('mrow.movedMain'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('mrow.confirmDelete', { name }))) return
    try {
      await deleteConv.mutateAsync(conv.id)
      toast.success(t('mrow.deleted'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <li className="group relative">
      {/* Ô tích nằm NGOÀI nút của dòng (không lồng nút trong nút), đè lên avatar
          kiểu Gmail: rê chuột tới dòng là hiện, bấm là chọn. */}
      <RowCheckbox
        checked={checked}
        alwaysVisible={selectMode}
        onToggle={() => (selectMode ? onToggle() : onStartSelect())}
      />
      <button
        type="button"
        {...longPress.handlers}
        onClick={() => {
          // Cú click tự phát sinh sau khi thả tay từ lần giữ — đã xử lý rồi.
          if (longPress.wasLongPress()) return
          if (selectMode) onToggle()
          else onClick()
        }}
        className={cn(
          // select-none + tắt callout: giữ trên điện thoại không bôi đen chữ.
          'flex w-full select-none items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent [-webkit-touch-callout:none]',
          active && !selectMode && 'bg-accent',
          selectMode && checked && 'bg-primary/10',
        )}
      >
        {/* Avatar nhường chỗ cho ô tích — mờ đi thay vì đẩy dòng lệch sang phải. */}
        <div
          className={cn(
            'relative transition-opacity',
            selectMode ? 'opacity-0' : '[@media(hover:hover)]:group-hover:opacity-0',
          )}
        >
          <Avatar className="h-10 w-10">
            {conv.contact?.avatarUrl && <AvatarImage src={conv.contact.avatarUrl} alt={name} />}
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
          {unread && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card" />
          )}
          <PlatformBadge platform={conv.channelAccount?.platform} title={channelTitle(conv.channelAccount)} />
        </div>

        <div className="min-w-0 flex-1">
          {/* Dòng 1: tên (cắt bằng …) + nhãn tài khoản (tô màu theo tài khoản, kiểu Lark) + thời gian */}
          <div className="flex items-baseline gap-2">
            {conv.isPinned && <Pin className="h-3 w-3 shrink-0 self-center text-primary" />}
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                unread ? 'font-semibold' : 'font-medium',
              )}
              title={name}
            >
              {name}
            </span>
            {/* Nhiều OA / Fanpage cùng loại → tên tài khoản kênh khách nhắn vào, tô màu riêng từng tài khoản để
                nhận ra ngay hội thoại nào thuộc tài khoản nào (giống thẻ màu của Lark), không cần đọc chữ. */}
            {accountName && conv.channelAccount && (
              <span
                className="inline-flex max-w-[60px] shrink-0 items-center rounded px-1 py-px text-[9px] font-medium leading-4"
                style={{ backgroundColor: `${accountColor(conv.channelAccount.id)}22`, color: accountColor(conv.channelAccount.id) }}
                title={channelTitle(conv.channelAccount)}
              >
                <span className="truncate">{accountName}</span>
              </span>
            )}
            {!conv.isReplied && (
              <span
                className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-warning"
                title={t('shared.unreplied')}
              />
            )}
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {formatRelativeTime(conv.lastMessageAt)}
            </span>
          </div>

          {/* Dòng 2: nội dung tin cuối (1 dòng, cắt bằng …) + số chưa đọc */}
          <div className="mt-0.5 flex items-center gap-1.5">
            {showAiAgent && conv.aiAgent && <AiAgentChip agent={conv.aiAgent} />}
            {tags.slice(0, 1).map((t) => {
              const color = tagColor(t)
              return (
                <span
                  key={t}
                  className="inline-flex max-w-[84px] shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: `${color}22`, color }}
                  title={tags.join(', ')}
                >
                  <Tag className="h-2.5 w-2.5 shrink-0" style={{ fill: color }} />
                  <span className="truncate">{t}</span>
                </span>
              )
            })}
            {tags.length > 1 && (
              <span className="shrink-0 text-[10px] text-muted-foreground" title={tags.join(', ')}>
                +{tags.length - 1}
              </span>
            )}

            <span
              className={cn(
                'min-w-0 flex-1 truncate text-xs',
                unread ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              {messagePreview(last)}
            </span>

            {unread && (
              <Badge
                variant="destructive"
                className="min-w-[18px] shrink-0 justify-center rounded-full px-1.5 py-0 text-[10px]"
              >
                {conv.unreadCount}
              </Badge>
            )}
          </div>
        </div>
      </button>

      {/* Menu hành động cuối dòng — hiện khi hover; ẩn khi đang chọn nhiều
          (thao tác lúc đó nằm ở thanh hàng loạt, tránh bấm nhầm từng dòng). */}
      <div className={cn(
        'absolute right-2 top-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
        selectMode && 'hidden',
      )}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('list.rowActions')}
              className="flex h-6 w-6 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-sm hover:bg-muted"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {/* Gắn nhãn: mở thẳng danh sách thẻ (submenu) — không popup */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={!conv.contact?.id}>
                <Tag className="mr-2 h-4 w-4" /> {t('bulk.addTag')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-96 w-60 overflow-y-auto">
                {allTags.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-muted-foreground">{t('bulk.noTags')}</div>
                ) : (
                  allTags.map((t) => {
                    const checked = tags.includes(t.name)
                    return (
                      <DropdownMenuItem
                        key={t.id}
                        onSelect={(e) => {
                          e.preventDefault() // giữ menu mở để gắn nhiều nhãn liên tiếp
                          void toggleTag(t.name)
                        }}
                        className="gap-2"
                      >
                        <Tag className="h-4 w-4 shrink-0" style={{ color: t.color, fill: t.color }} />
                        <span className="flex-1 truncate">{t.name}</span>
                        {savingTag === t.name ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : checked ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : null}
                      </DropdownMenuItem>
                    )
                  })
                )}
                {canManageTags && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setManageTagsOpen(true)} className="gap-2">
                      <Settings2 className="h-4 w-4" /> {t('list.manageTags')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={() => void handleMarkUnread()}>
              <MailOpen className="h-4 w-4" /> {t('list.markUnread')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void handleMoveTab()} disabled={setTab.isPending}>
              {isInternal ? (
                <><Star className="h-4 w-4" /> {t('mrow.toMain')}</>
              ) : (
                <><Users className="h-4 w-4" /> {t('mrow.toInternal')}</>
              )}
            </DropdownMenuItem>
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => void handleDelete()}
                >
                  <Trash2 className="h-4 w-4" /> {t('mrow.delete')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Popup quản lý thẻ phân loại (mở từ submenu) */}
      <ManageTagsDialog open={manageTagsOpen} onOpenChange={setManageTagsOpen} tags={allTags} />
    </li>
  )
}
